// src/Components/Nav_menu.js

import React, { useState } from "react";
// Link is a special component from React Router that lets us
// navigate without reloading the entire page.
import { Link } from "react-router-dom";

// Import the CSS file that contains all menu styling.
// You can open this file to tweak colors, spacing, etc.
import "../CSS_stuff/Menu_styles.css";

// These two lines load the banner images for desktop and mobile.
// Think of them like <img src="..."> but brought into our code.
import bannerSrc       from "../assets/Website-top-banner-long.jpg";
import mobileBannerSrc from "../assets/Website-top-banner.jpg";

// This is the main function that defines our navigation menu.
// We export it so other parts of our app can show it at the top.
export default function NavMenu() {
  // ─────────────────────────────────────────────────────────────────────────────
  // State variables: pieces of data that remember if menus are open
  //
  // menuOpen: true if the main nav links (About, Contact, etc.) are visible
  // fictionOpen: true if the "Fiction by series" dropdown is expanded
  // ─────────────────────────────────────────────────────────────────────────────
  const [menuOpen, setMenuOpen]       = useState(false);
  const [fictionOpen, setFictionOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // toggleMenu: called when the hamburger button is clicked.
  //   - Flips menuOpen between true/false.
  //   - If we’re closing the menu, also close the fiction dropdown.
  // ─────────────────────────────────────────────────────────────────────────────
  const toggleMenu = () => {
    setMenuOpen(open => !open);  // invert the menuOpen value
    if (menuOpen) {
      setFictionOpen(false);     // if closing menu, also close dropdown
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // closeAll: called whenever we click a link.
  //   - Hides both the main menu and the fiction dropdown.
  // ─────────────────────────────────────────────────────────────────────────────
  const closeAll = () => {
    setMenuOpen(false);
    setFictionOpen(false);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // The JSX below describes *what* the menu looks like.
  // Think of it as nested HTML with a bit of logic.
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <header>
      {/* ── Privacy Policy Link ──
            This always shows at the very top, above the banner. */}
      <div className="privacy-policy-link">
        {/* Link to the /privacy-policy page; clicking it also closes menus */}
        <Link to="/privacy-policy" onClick={closeAll}>
          By using our website you are agreeing to our Privacy Policy
        </Link>
      </div>

      {/* ── Site Banner ──
            A big image (desktop or mobile) that links back to home. */}
      <div className="site-banner">
        <Link to="/" onClick={closeAll}>
          <picture>
            {/* If the screen is 767px wide or smaller, use mobileBannerSrc */}
            <source media="(max-width: 767px)" srcSet={mobileBannerSrc} />
            {/* Otherwise, show the regular banner */}
            <img
              src={bannerSrc}
              alt="Home banner"
              className="page-banner"
            />
          </picture>
        </Link>
      </div>

      {/* ── Main Navigation Bar ── */}
      <nav className="navbar" aria-label="Main navigation">
        {/* Hamburger button for mobile: toggles the main list of links */}
        <button
          className="navbar-toggle"
          aria-label="Toggle navigation menu"
          aria-controls="primary-navigation"
          aria-expanded={menuOpen}  // accessibility: true/false
          onClick={toggleMenu}      // run our toggleMenu function
        >
          ☰
        </button>

        {/* 
          The actual list of links.
          We add the “open” class when menuOpen is true so CSS can show/hide.
        */}
        <ul
          id="primary-navigation"
          className={`navbar-links ${menuOpen ? "open" : ""}`}
        >
          {/* Simple links that call closeAll when clicked */}
          <li><Link to="/about"      onClick={closeAll}>About</Link></li>
          <li><Link to="/contact"    onClick={closeAll}>Contact</Link></li>
          <li><Link to="/newsletter" onClick={closeAll}>Newsletter</Link></li>

          {/* ── Fiction dropdown ── */}
          <li
            className={`dropdown ${fictionOpen ? "open" : ""}`}
            // On desktop hover, open/close the dropdown
            onMouseEnter={() => setFictionOpen(true)}
            onMouseLeave={() => setFictionOpen(false)}
          >
            {/* Desktop-only link (hover) */}
            <Link
              to="/fiction"
              className="dropdown-toggle desktop-only"
              onClick={closeAll}
            >
              Fiction by series ▼
            </Link>

            {/* Mobile-only button (tap) */}
            <button
              type="button"
              className="dropdown-toggle mobile-only"
              aria-haspopup="true"
              aria-expanded={fictionOpen}
              onClick={() => setFictionOpen(o => !o)}
            >
              Fiction by series ▼
            </button>

            {/* The dropdown menu items */}
            <ul className="dropdown-menu">
              <li>
                <Link to="/fiction#chronicles-of-ilseador" onClick={closeAll}>
                  Chronicles of Ilseador
                </Link>
              </li>
              <li>
                <Link to="/fiction#knightess-of-the-realm" onClick={closeAll}>
                  Knightess of the Realm
                </Link>
              </li>
              <li>
                <Link to="/fiction#prankster-prince" onClick={closeAll}>
                  Prankster Prince
                </Link>
              </li>
              <li>
                <Link to="/fiction#ContemporaryRomance" onClick={closeAll}>
                  Contemporary Romance
                </Link>
              </li>
              <li>
                <Link to="/lore#read-order" onClick={closeAll}>
                  Click here for Suggested Reading Order
                </Link>
              </li>
            </ul>
          </li>

          {/* More top‐level links */}
          <li><Link to="/catalog"        onClick={closeAll}>Full Catalog</Link></li>
          <li><Link to="/upcoming-books" onClick={closeAll}>Upcoming Books</Link></li>
          <li><Link to="/upcoming-events" onClick={closeAll}>Events</Link></li>
          <li><Link to="/lore"           onClick={closeAll}>Lore</Link></li>
        </ul>
      </nav>
    </header>
  );
}